/**
 * PATH: apps/qa/src/lib/api/templates.ts
 * PURPOSE: QA Checklist Templates API
 */

import { api, normaliseArray } from './helpers'
import type { ChecklistTemplate } from '@/types/qa'
import type { TemplateItem, TemplateSection } from '@/pages/template-detail/types'

export interface TemplateFilters {
  status?: string
  trade?: string
}

export const templateApi = {
  getTemplates: async (params?: TemplateFilters): Promise<ChecklistTemplate[]> => {
    const response = await api().get('/qa/templates/', { params })
    return normaliseArray<ChecklistTemplate>(response.data)
  },
  getTemplate: async (id: string): Promise<ChecklistTemplate> => {
    const response = await api().get(`/qa/templates/${id}/`)
    return response.data
  },
  createTemplate: async (data: Partial<ChecklistTemplate>): Promise<ChecklistTemplate> => {
    const response = await api().post('/qa/templates/', data)
    return response.data
  },
  updateTemplate: async (id: string, data: Partial<ChecklistTemplate>): Promise<ChecklistTemplate> => {
    const response = await api().patch(`/qa/templates/${id}/`, data)
    return response.data
  },
  deleteTemplate: async (id: string): Promise<void> => {
    await api().delete(`/qa/templates/${id}/`)
  },
  duplicateTemplate: async (id: string, name: string): Promise<ChecklistTemplate> => {
    const response = await api().post(`/qa/templates/${id}/duplicate/`, { name })
    return response.data
  },
  forkTemplate: async (id: string, name?: string): Promise<ChecklistTemplate> => {
    const response = await api().post(`/qa/templates/${id}/fork/`, name ? { name } : {})
    return response.data
  },
  publishTemplate: async (id: string): Promise<ChecklistTemplate> => {
    const response = await api().post(`/qa/templates/${id}/publish/`)
    return response.data
  },
  createSection: async (templateId: string, section: Omit<TemplateSection, 'id' | '_localId' | 'items'>): Promise<TemplateSection> => {
    const response = await api().post('/qa/template-sections/', { ...section, template: templateId })
    return response.data
  },
  updateSection: async (sectionId: string, section: Partial<TemplateSection>): Promise<TemplateSection> => {
    const response = await api().patch(`/qa/template-sections/${sectionId}/`, section)
    return response.data
  },
  deleteSection: async (sectionId: string): Promise<void> => {
    await api().delete(`/qa/template-sections/${sectionId}/`)
  },
  createItem: async (sectionId: string, item: Omit<TemplateItem, 'id' | '_localId'>): Promise<TemplateItem> => {
    const response = await api().post('/qa/template-items/', { ...item, section: sectionId })
    return response.data
  },
  updateItem: async (itemId: string, item: Partial<TemplateItem>): Promise<TemplateItem> => {
    const response = await api().patch(`/qa/template-items/${itemId}/`, item)
    return response.data
  },
  deleteItem: async (itemId: string): Promise<void> => {
    await api().delete(`/qa/template-items/${itemId}/`)
  },
}
